// 1. React
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers
import { useTranslate } from "helpers";

// 3. Types
import { OfferItemsProps } from "./types";
import { Errors } from "services/forms/validation/types";
import { FleetVehicleOfferRentItemEditFormValues, FleetVehicleOfferRentItemRow } from "services/api/fleet/vehicles/offers/types";
import { FleetVehicle } from "services/api/fleet/vehicles/types";

// 4. API / Consts
import { initialFleetVehicleOfferRentItemEditForm } from "services/api/fleet/vehicles/offers/consts";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { getBillingItemTypesAPI } from "services/api/finance/billing/api";
import { BillingItemKindId } from "services/api/finance/billing/consts";
import { BillingItemType } from "services/api/finance/billing/types";
import { transformOfferRentItemRow, transformOfferRentItemRowToForm } from "services/api/fleet/vehicles/offers/transformations";

// 5. UI Components
import OfferItemsAdded from "./OfferItemsAdded";
import { OfferItemForm } from "./OfferItemForm";

// 6. Validation
import { validateYup } from "services/forms/validation/validation";
import { FLEET_VEHICLE_OFFER_RENT_ITEM_EDIT_FORM_SCHEMA } from "./validation";
import { Dialog } from "ui/components/Dialog";
import { Button } from "ui/components/Button/Button";


const OfferItems: React.FC<OfferItemsProps> = (props) => {
    const { formValues, setFormValues } = props;

    const { t } = useTranslate();

    const [itemFormAdd, setItemFormAdd] =
        useState<FleetVehicleOfferRentItemEditFormValues>(
            { ...initialFleetVehicleOfferRentItemEditForm }
        );

    const [itemId, setItemId] = useState<string>("");
    const [isEdit, setIsEdit] = useState(false);
    const [showItemFormModal, setShowItemFormModal] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [errors, setErrors] = useState<Errors>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (formValues.vehicleId) fetchVehicle(formValues.vehicleId);
    }, [formValues.vehicleId]);

    /** Vrsta stavke za najamninu iz šifrarnika — bez nje se stavka ne prepoznaje kao ponavljajuća. */
    const fetchRentItemType = async (): Promise<BillingItemType | null> => {
        try {
            const itemTypes = await getBillingItemTypesAPI(BillingItemKindId.Najamnina);
            return itemTypes.find(itemType => itemType.isRecurring) ?? null;
        } catch {
            return null;
        }
    };

    const fetchVehicle = async (id: string) => {
        if (id === undefined) {
            return;
        }

        setIsLoading(true);

        let result: FleetVehicle;
        try {
            result = await getFleetVehicleAPI(id);
        } catch {
            setIsLoading(false);
            return;
        }

        if (result && formValues.items && formValues.items.length === 0) {
            // Ponuda za najam počinje najamninom tog vozila — cijenu upisuje korisnik.
            // Vrsta iz šifrarnika (ponavljajuća) je bitna: samo se takva stavka kasnije
            // automatski prenosi s ugovora na mjesečni račun.
            const rentType = await fetchRentItemType();

            const rentItem = transformOfferRentItemRow({
                ...initialFleetVehicleOfferRentItemEditForm,
                id: `rent-${Date.now()}`,
                billingItemTypeId: rentType?.id ?? null,
                code: rentType?.code ?? "NAJAM",
                name: `${rentType?.name ?? "Najam vozila"} - ${result.name}`,
                quantity: rentType?.defaultQuantity ?? 1,
                taxRate: rentType?.defaultTaxRate ?? 25,
                unitOfMeasureId: rentType?.defaultUnitOfMeasureId ?? "mjesec",
                kpdCode: rentType?.defaultKpdCode ?? null,
                priceWithoutTax: null,
                priceWithTax: null,
                total: null,
            });

            setFormValues(prev => ({
                ...prev,
                items: [rentItem],
            }));

            // Najamnina je pretpunjena, ali bez cijene — pa se stavka odmah otvori na uređivanje
            // umjesto da korisnik traži gdje se cijena upisuje.
            setItemFormAdd(transformOfferRentItemRowToForm(rentItem));
            setItemId(rentItem.id);
            setIsEdit(true);
            setShowItemFormModal(true);
        }


        setIsLoading(false);
        setErrors({});
    };

    const handleAddItem = async (e: any) => {
        e.preventDefault();

        const isValid = validateYup(
            itemFormAdd,
            FLEET_VEHICLE_OFFER_RENT_ITEM_EDIT_FORM_SCHEMA,
            setErrors
        );

        if (!isValid) {
            return;
        }

        setIsLoading(true);

        if (isEdit) {
            setFormValues(prev => ({
                ...prev,
                items: prev.items.map(item =>
                    item.id === itemFormAdd.id
                        ? transformOfferRentItemRow(itemFormAdd)
                        : item
                ),
            }));
        }
        else {
            setFormValues(prev => ({
                ...prev,
                items: [...prev.items, transformOfferRentItemRow(itemFormAdd)]
            }));
        }

        setItemFormAdd(initialFleetVehicleOfferRentItemEditForm);
        setIsEdit(false);
        setItemId("");
        setIsLoading(false);
        setShowItemFormModal(false);
    };

    const handleAddClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        setItemFormAdd({ ...initialFleetVehicleOfferRentItemEditForm, id: crypto.randomUUID() });
        setItemId("");
        setIsEdit(false);
        setErrors({});
        setShowItemFormModal(true);
    };
    const handleEditClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        let item = formValues.items.find(item => item.id === id)

        if (!item)
            return;

        setItemFormAdd(transformOfferRentItemRowToForm(item));
        setItemId(id);
        setIsEdit(true);
        setShowItemFormModal(true);
    }

    const handleItemFormModalClose = () => {
        setShowItemFormModal(false);
        setItemFormAdd(initialFleetVehicleOfferRentItemEditForm);
        setIsEdit(false);
        setItemId("");
        setErrors({});
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();
        setItemId(id);
        setOpenDialog(true);
    }

    const handleDeleteDialogConfirm = useCallback(async () => {
        if (!itemId) return;

        setFormValues(prev => ({
            ...prev,
            items: prev.items.map(item =>
                item.id === itemId
                    ? { ...item, deleted: true }
                    : item
            ),
        }));


        setOpenDialog(false);
        setItemId("");

    }, [itemId, t]);

    const handleDeleteDialogClose = useCallback(() => {
        setItemId("");
        setOpenDialog(false);
    }, []);


    const items = formValues.items.filter(i => !i.deleted);

    return (
        <>
            <div className="p-3">
                <div className="flex justify-end mb-3">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon="/media/icons/plus.svg"
                        onClick={handleAddClick}
                    >
                        Dodaj stavku
                    </Button>
                </div>
                {items && items.length > 0 && (
                    <OfferItemsAdded
                        isLoading={false}
                        items={items}
                        onEditClick={handleEditClick}
                        onDeleteClick={handleDeleteClick}
                    />
                )}
            </div>
            <OfferItemForm
                show={showItemFormModal}
                onHide={handleItemFormModalClose}
                itemFormAdd={itemFormAdd}
                setItemFormAdd={setItemFormAdd}
                errors={errors}
                isEdit={isEdit}
                onSubmit={handleAddItem}
            />
            <Dialog
                open={openDialog}
                title={t(
                    "admin.fleet.vehicle.offer.rent.item.dialog.delete.title"
                )}
                message={t(
                    "admin.fleet.vehicle.offer.rent.item.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.fleet.vehicle.offer.rent.item.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.fleet.vehicle.offer.rent.item.dialog.delete.canceltext"
                )}
                onConfirm={handleDeleteDialogConfirm}
                onCancel={handleDeleteDialogClose}
            />
        </>
    );
};

export default memo(OfferItems);
